import { Link, useNavigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import swal from 'sweetalert';
import { PlusIcon } from '@heroicons/react/solid';
import {
    CreditCardIcon,
    ChevronRightIcon,
} from '@heroicons/react/outline';
import LoadingBar from 'react-top-loading-bar';
import { useIsOnline } from 'react-use-is-online';
import { selectCurrentUser } from '../../Features/auth/authSlice';
import { useSelector } from 'react-redux';
import { useGetUserPaymentTypesQuery } from '../../Features/paymentTypes';

function ManagePaymentTypesView({}) {
    const navigate = useNavigate();
    const [progress, setProgress] = useState(0);
    const { isOnline } = useIsOnline();
    const [currentItems, setCurrentItems] = useState([]);

    const user = useSelector(selectCurrentUser);

    const {
        data,
        isLoading,
        isSuccess,
        isError,
        error
    } = useGetUserPaymentTypesQuery({ user_id: user.id });

    useEffect(() => {
        setProgress(30);

        if (isLoading) {
            setProgress(80);
        }

        if (isSuccess && !currentItems.length > 0) {
            setCurrentItems(data.data);
        }

        setProgress(100);

        return () => {
        };
    }, [data]);

    const edit = (e, id) => {
        if (!isOnline) {
            return swal(
                "No Internet connection",
                'Please check Internet connection and try again'
            );
        }

        navigate("/client/edit-payment-type/" + id);
    };

    const trancateText = (str) => {
        return str.length > 25
            ? str.substring(0, 25) + "..."
            : str;
    };

    return (
        <div className="w-full min-h-screen bg-gray-100 dark:bg-gray-950 pb-12">

            <LoadingBar
                color="#80b643"
                height="4px"
                progress={progress}
                onLoaderFinished={() => setProgress(0)}
            />

            <div className="w-full px-4 sm:px-6 lg:px-8 pt-6">

                {/* Header */}
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">

                    <div className="flex items-center gap-3">

                        <div className="w-11 h-11 rounded-xl bg-[#80b643]/10 dark:bg-[#80b643]/20 flex items-center justify-center">
                            <CreditCardIcon className="w-6 h-6 text-[#80b643]" />
                        </div>

                        <div>
                            <h1 className="text-lg sm:text-xl font-semibold text-gray-900 dark:text-white">
                                Payment Types
                            </h1>

                            <p className="text-sm text-gray-500 dark:text-gray-400">
                                Manage the payment methods available to your business
                            </p>
                        </div>

                    </div>

                    {isSuccess && (
                        <Link
                            to="/client/create-payment-type"
                            className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-[#80b643] hover:bg-[#719f3a] text-white text-sm font-medium shadow-sm transition"
                        >
                            <PlusIcon className="w-4 h-4" />
                            Add Payment Type
                        </Link>
                    )}

                </div>

                {/* Main Card */}
                <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl shadow-sm overflow-hidden">

                    {/* Card Header */}
                    <div className="px-5 sm:px-6 py-4 border-b border-gray-200 dark:border-gray-800">

                        <div className="flex items-center justify-between">

                            <div>
                                <h2 className="text-sm font-semibold text-gray-900 dark:text-white">
                                    Payment Methods
                                </h2>

                                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                    Select a payment type to edit its details
                                </p>
                            </div>

                            {isSuccess && (
                                <div className="hidden sm:flex items-center px-3 py-1.5 rounded-lg bg-[#80b643]/10 dark:bg-[#80b643]/20">
                                    <span className="text-xs font-medium text-[#80b643]">
                                        {currentItems.length}{" "}
                                        {currentItems.length === 1
                                            ? "Payment Type"
                                            : "Payment Types"}
                                    </span>
                                </div>
                            )}

                        </div>

                    </div>

                    {/* Loading */}
                    {isLoading ? (

                        <div className="px-5 sm:px-6 py-12">

                            <div className="flex flex-col items-center justify-center">

                                <div className="w-10 h-10 border-4 border-gray-200 dark:border-gray-700 border-t-[#80b643] rounded-full animate-spin mb-4"></div>

                                <p className="text-sm font-medium text-gray-700 dark:text-gray-300">
                                    Loading payment types...
                                </p>

                                <p className="text-xs text-gray-500 dark:text-gray-500 mt-1">
                                    Please wait
                                </p>

                            </div>

                        </div>

                    ) : currentItems.length > 0 ? (

                        <>
                            {/* Desktop Table */}
                            <div className="hidden md:block overflow-x-auto">

                                <table className="w-full">

                                    <thead className="bg-gray-50 dark:bg-gray-800/70">

                                        <tr>

                                            <th
                                                scope="col"
                                                className="px-6 py-3.5 text-left text-[11px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider"
                                            >
                                                Payment Type
                                            </th>

                                            <th
                                                scope="col"
                                                className="px-6 py-3.5 text-right text-[11px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider"
                                            >
                                                Action
                                            </th>

                                        </tr>

                                    </thead>

                                    <tbody className="divide-y divide-gray-100 dark:divide-gray-800">

                                        {currentItems.map((data) => (

                                            <tr
                                                onClick={(e) => edit(e, data.id)}
                                                key={data.id}
                                                className="group hover:bg-gray-50 dark:hover:bg-gray-800/50 cursor-pointer transition"
                                            >

                                                <td className="px-6 py-4">

                                                    <div className="flex items-center gap-3">

                                                        <div className="w-9 h-9 rounded-lg bg-[#80b643]/10 dark:bg-[#80b643]/20 flex items-center justify-center">

                                                            <CreditCardIcon className="w-5 h-5 text-[#80b643]" />

                                                        </div>

                                                        <div>

                                                            <p className="text-sm font-medium text-gray-900 dark:text-white">
                                                                {trancateText(data.name)}
                                                            </p>

                                                            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                                                                Payment method
                                                            </p>

                                                        </div>

                                                    </div>

                                                </td>

                                                <td className="px-6 py-4 text-right">

                                                    <div className="inline-flex items-center gap-1 text-xs font-medium text-gray-500 dark:text-gray-400 group-hover:text-[#80b643] transition">

                                                        Edit

                                                        <ChevronRightIcon className="w-4 h-4" />

                                                    </div>

                                                </td>

                                            </tr>

                                        ))}

                                    </tbody>

                                </table>

                            </div>

                            {/* Mobile List */}
                            <div className="md:hidden divide-y divide-gray-100 dark:divide-gray-800">

                                {currentItems.map((data) => (

                                    <div
                                        key={data.id}
                                        onClick={(e) => edit(e, data.id)}
                                        className="flex items-center justify-between px-4 py-4 hover:bg-gray-50 dark:hover:bg-gray-800/50 cursor-pointer transition"
                                    >

                                        <div className="flex items-center gap-3 min-w-0">

                                            <div className="w-9 h-9 shrink-0 rounded-lg bg-[#80b643]/10 dark:bg-[#80b643]/20 flex items-center justify-center">

                                                <CreditCardIcon className="w-5 h-5 text-[#80b643]" />

                                            </div>

                                            <div className="min-w-0">

                                                <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                                                    {trancateText(data.name)}
                                                </p>

                                                <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                                                    Payment method
                                                </p>

                                            </div>

                                        </div>

                                        <ChevronRightIcon className="w-5 h-5 shrink-0 text-gray-400" />

                                    </div>

                                ))}

                            </div>
                        </>

                    ) : isError ? (

                        <div className="px-5 sm:px-6 py-12">

                            <div className="flex flex-col items-center justify-center text-center">

                                <div className="w-12 h-12 rounded-xl bg-gray-100 dark:bg-gray-800 flex items-center justify-center mb-4">

                                    <CreditCardIcon className="w-6 h-6 text-gray-400" />

                                </div>

                                <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
                                    No payment types found
                                </h3>

                                <p className="text-sm text-gray-500 dark:text-gray-400 mt-1 mb-5">
                                    Create your first payment type to get started.
                                </p>

                                <Link
                                    to="/client/create-payment-type"
                                    className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-[#80b643] hover:bg-[#719f3a] text-white text-sm font-medium shadow-sm transition"
                                >
                                    <PlusIcon className="w-4 h-4" />
                                    Add Payment Type
                                </Link>

                            </div>

                        </div>

                    ) : (

                        <div className="px-5 sm:px-6 py-12">

                            <div className="flex flex-col items-center justify-center text-center">

                                <div className="w-14 h-14 rounded-xl bg-gray-100 dark:bg-gray-800 flex items-center justify-center mb-4">

                                    <CreditCardIcon className="w-7 h-7 text-gray-400" />

                                </div>

                                <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
                                    No payment types yet
                                </h3>

                                <p className="text-sm text-gray-500 dark:text-gray-400 mt-1 mb-5 max-w-sm">
                                    Add a payment type such as Cash, Mobile Money, Card, or Bank Transfer.
                                </p>

                                <Link
                                    to="/client/create-payment-type"
                                    className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-[#80b643] hover:bg-[#719f3a] text-white text-sm font-medium shadow-sm transition"
                                >
                                    <PlusIcon className="w-4 h-4" />
                                    Add Payment Type
                                </Link>

                            </div>

                        </div>

                    )}

                </div>

            </div>

        </div>
    );
}

export default ManagePaymentTypesView;